import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';

import { fetchWrapper } from '../../../helpers';
import { getSerializedErrorMessage } from '../../../utils/errors';
import { getUtcOffset } from '../../../utils/common';
import { buildQuestionnaireHistoryUrl, redirectToDashboardOnClientError } from '../utils';
import { INTAKE_QUESTIONNAIRES_API_BASE } from './constants';
import { addIntakeChildCases, createIntakeChildThunks } from './pages/child';
import { addIntakeEducationCases, createIntakeEducationThunks } from './pages/education';
import { addIntakeFamilyCases, createIntakeFamilyThunks } from './pages/family';
import { addIntakeGeneralCases, createIntakeGeneralThunks } from './pages/general';
import { addIntakeMedicalCases, createIntakeMedicalThunks } from './pages/medical';
import { addIntakeMedicationCases, createIntakeMedicationThunks } from './pages/medication';
import { addIntakeReferralCases, createIntakeReferralThunks } from './pages/referral';
import { addIntakeTherapyCases, createIntakeTherapyThunks } from './pages/therapy';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  GetIntakeQuestionnaireByIdPayload,
  GetIntakeQuestionnairesByProviderIdPayload,
  GetIntakeQuestionnairesPayload,
  IIntakeQuestionnaireBasic,
  IIntakeQuestionnaireDetails,
  IntakeQuestionnaireState,
  SendIntakeQuestionnairePayload,
  SubmitIntakeQuestionnairePayload,
  UpdateIntakeTabStatusPayload,
} from './types';

const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const slice = createSlice({
  name: 'intakeQuestionnaire',
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<IntakeQuestionnaireState>) =>
    createExtraReducers(builder),
});

function createInitialState(): IntakeQuestionnaireState {
  return {
    tabStatuses: {
      introduction: { error: false, success: false },
      general: { error: false, success: false },
      medical: { error: false, success: false },
      referral: { error: false, success: false },
      medication: { error: false, success: false },
      therapy: { error: false, success: false },
      education: { error: false, success: false },
      family: { error: false, success: false },
      child: { error: false, success: false },
      submit: { error: false, success: false },
    },
    error: null,
    intakeQuestionnaireDetails: null,
    questionnaireDetailsLoading: false,
    questionnaireDetailsRequestId: null,
    updateGeneralInfoLoading: false,
    pendingGeneralInfoUpdateCount: 0,
    createMedicationLoading: false,
    updateMedicationLoading: false,
    deleteMedicationLoading: false,
    deleteAllMedicationsLoading: false,
    setHasNoMedicationsLoading: false,
    createReferralLoading: false,
    updateReferralLoading: false,
    deleteReferralLoading: false,
    createTherapyLoading: false,
    updateTherapyLoading: false,
    deleteTherapyLoading: false,
    deleteAllTherapiesLoading: false,
    setHasNoTherapiesLoading: false,
    updateMedicalInfoLoading: false,
    createMedicalDiagnosisLoading: false,
    updateMedicalDiagnosisLoading: false,
    deleteMedicalDiagnosisLoading: false,
    createMedicalSelectionLoading: false,
    deleteMedicalSelectionLoading: false,
    updateEducationInfoLoading: false,
    setNotAttendingSchoolLoading: false,
    updateFamilyLoading: false,
    createFamilyMemberLoading: false,
    updateFamilyMemberLoading: false,
    deleteFamilyMemberLoading: false,
    createClientSiblingLoading: false,
    updateClientSiblingLoading: false,
    deleteClientSiblingLoading: false,
    createFamilyAdditionalInfoLoading: false,
    deleteFamilyAdditionalInfoLoading: false,
    updateChildInfoLoading: false,
    createChildBackgroundLoading: false,
    deleteChildBackgroundLoading: false,
    createChildScheduleLoading: false,
    deleteChildScheduleLoading: false,
    intakeQuestionnairesBasicInfo: [],
    intakeQuestionnairesBasicInfoLoading: false,
    intakeQuestionnairesBasicInfoRequestId: null,
    intakeQuestionnairesBasicInfoProviderId: null,
    intakeQuestionnaires: [],
    intakeQuestionnairesLoading: false,
    sendIntakeQuestionnaireLoading: false,
    submitIntakeQuestionnaireLoading: false,
  };
}

function createReducers() {
  return {
    updateIntakeTabStatus,
    clearIntakeQuestionnaires,
    clearIntakeQuestionnairesBasicInfo,
  };

  function clearIntakeQuestionnaires(state: IntakeQuestionnaireState) {
    state.intakeQuestionnaires = [];
    state.intakeQuestionnairesLoading = false;
  }

  function clearIntakeQuestionnairesBasicInfo(state: IntakeQuestionnaireState) {
    state.intakeQuestionnairesBasicInfo = [];
    state.intakeQuestionnairesBasicInfoLoading = false;
    state.intakeQuestionnairesBasicInfoRequestId = null;
    state.intakeQuestionnairesBasicInfoProviderId = null;
  }

  function updateIntakeTabStatus(
    state: IntakeQuestionnaireState,
    action: PayloadAction<UpdateIntakeTabStatusPayload>,
  ) {
    const { tabId, status } = action.payload;
    const currentStatus = state.tabStatuses[tabId];

    if (currentStatus.error === status.error && currentStatus.success === status.success) {
      return;
    }

    state.tabStatuses[tabId] = status;
  }
}

function createExtraActions() {
  return {
    ...createIntakeQuestionnaireThunks(),
    ...createIntakeGeneralThunks(),
    ...createIntakeMedicationThunks(),
    ...createIntakeReferralThunks(),
    ...createIntakeTherapyThunks(),
    ...createIntakeMedicalThunks(),
    ...createIntakeEducationThunks(),
    ...createIntakeFamilyThunks(),
    ...createIntakeChildThunks(),
  };
}

function createIntakeQuestionnaireThunks() {
  return {
    getIntakeQuestionnaireById: getIntakeQuestionnaireById(),
    getIntakeQuestionnairesByProviderId: getIntakeQuestionnairesByProviderId(),
    getIntakeQuestionnaires: getIntakeQuestionnaires(),
    sendIntakeQuestionnaire: sendIntakeQuestionnaire(),
    submitIntakeQuestionnaire: submitIntakeQuestionnaire(),
  };
}

function getIntakeQuestionnaireById() {
  return createAsyncThunk<IIntakeQuestionnaireDetails, GetIntakeQuestionnaireByIdPayload>(
    'intakeQuestionnaire/getIntakeQuestionnaireById',
    async ({ intakeQuestionnaireId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}`,
        getAccessTokenSilently,
      }),
  );
}

function getIntakeQuestionnairesByProviderId() {
  return createAsyncThunk<IIntakeQuestionnaireBasic[], GetIntakeQuestionnairesByProviderIdPayload>(
    'intakeQuestionnaire/getIntakeQuestionnairesByProviderId',
    async ({ providerId, getAccessTokenSilently }) =>
      // Filter the list endpoint by provider rather than calling `by_provider/{id}`: both
      // return the same rows, but only list populates the nested `client` summary that
      // consumers need for the client name. Passing a filter also opts out of the list
      // endpoint's default past-month window.
      fetchWrapper.get({
        url: buildQuestionnaireHistoryUrl(INTAKE_QUESTIONNAIRES_API_BASE, {
          provider_id: providerId,
        }),
        getAccessTokenSilently,
      }),
  );
}

function getIntakeQuestionnaires() {
  return createAsyncThunk<IIntakeQuestionnaireBasic[], GetIntakeQuestionnairesPayload>(
    'intakeQuestionnaire/getIntakeQuestionnaires',
    async ({ params, getAccessTokenSilently, signal }) =>
      fetchWrapper.get({
        url: buildQuestionnaireHistoryUrl(INTAKE_QUESTIONNAIRES_API_BASE, params),
        getAccessTokenSilently,
        signal,
      }),
  );
}

function sendIntakeQuestionnaire() {
  return createAsyncThunk<IIntakeQuestionnaireBasic, SendIntakeQuestionnairePayload>(
    'intakeQuestionnaire/sendIntakeQuestionnaire',
    async ({ data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/send`,
          getAccessTokenSilently,
        },
        {
          ...data,
          date_sent_utc_offset: getUtcOffset(moment()),
        },
      ),
  );
}

function submitIntakeQuestionnaire() {
  return createAsyncThunk<IIntakeQuestionnaireBasic, SubmitIntakeQuestionnairePayload>(
    'intakeQuestionnaire/submitIntakeQuestionnaire',
    async ({ intakeQuestionnaireId, data, getAccessTokenSilently }) =>
      fetchWrapper.put(
        {
          url: `${INTAKE_QUESTIONNAIRES_API_BASE}/${intakeQuestionnaireId}/submit`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createExtraReducers(builder: ActionReducerMapBuilder<IntakeQuestionnaireState>) {
  addIntakeQuestionnaireCases(builder);
  addIntakeGeneralCases(builder, extraActions);
  addIntakeMedicationCases(builder, extraActions);
  addIntakeReferralCases(builder, extraActions);
  addIntakeTherapyCases(builder, extraActions);
  addIntakeMedicalCases(builder, extraActions);
  addIntakeEducationCases(builder, extraActions);
  addIntakeFamilyCases(builder, extraActions);
  addIntakeChildCases(builder, extraActions);
}

function addIntakeQuestionnaireCases(builder: ActionReducerMapBuilder<IntakeQuestionnaireState>) {
  addGetIntakeQuestionnaireByIdCases(builder);
  addGetIntakeQuestionnairesByProviderIdCases(builder);
  addGetIntakeQuestionnairesCases(builder);
  addSendIntakeQuestionnaireCases(builder);
  addSubmitIntakeQuestionnaireCases(builder);
}

function addGetIntakeQuestionnaireByIdCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
) {
  const { getIntakeQuestionnaireById } = extraActions;

  builder
    .addCase(getIntakeQuestionnaireById.pending, (state, action) => {
      if (
        state.intakeQuestionnaireDetails &&
        String(state.intakeQuestionnaireDetails.id) !== action.meta.arg.intakeQuestionnaireId
      ) {
        state.intakeQuestionnaireDetails = null;
      }
      state.questionnaireDetailsLoading = true;
      state.questionnaireDetailsRequestId = action.meta.requestId;
      state.error = null;
    })
    .addCase(getIntakeQuestionnaireById.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.questionnaireDetailsRequestId) return;
      state.questionnaireDetailsLoading = false;
      state.intakeQuestionnaireDetails = action.payload;
    })
    .addCase(getIntakeQuestionnaireById.rejected, (state, action) => {
      if (action.meta.requestId !== state.questionnaireDetailsRequestId) return;
      state.questionnaireDetailsLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
      redirectToDashboardOnClientError(action.error.code);
    });
}

function addGetIntakeQuestionnairesByProviderIdCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
) {
  const { getIntakeQuestionnairesByProviderId } = extraActions;

  builder
    .addCase(getIntakeQuestionnairesByProviderId.pending, (state, action) => {
      state.intakeQuestionnairesBasicInfo = [];
      state.intakeQuestionnairesBasicInfoLoading = true;
      state.intakeQuestionnairesBasicInfoRequestId = action.meta.requestId;
      state.intakeQuestionnairesBasicInfoProviderId = action.meta.arg.providerId;
    })
    .addCase(getIntakeQuestionnairesByProviderId.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.intakeQuestionnairesBasicInfoRequestId) return;
      state.intakeQuestionnairesBasicInfoLoading = false;
      state.intakeQuestionnairesBasicInfo = action.payload;
    })
    .addCase(getIntakeQuestionnairesByProviderId.rejected, (state, action) => {
      if (action.meta.requestId !== state.intakeQuestionnairesBasicInfoRequestId) return;
      if (action.meta.aborted) return;
      state.intakeQuestionnairesBasicInfoLoading = false;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addGetIntakeQuestionnairesCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
) {
  const { getIntakeQuestionnaires } = extraActions;

  builder
    .addCase(getIntakeQuestionnaires.pending, (state) => {
      state.intakeQuestionnairesLoading = true;
    })
    .addCase(
      getIntakeQuestionnaires.fulfilled,
      (state, action: PayloadAction<IIntakeQuestionnaireBasic[]>) => {
        state.intakeQuestionnairesLoading = false;
        state.intakeQuestionnaires = action.payload;
      },
    )
    .addCase(getIntakeQuestionnaires.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.intakeQuestionnairesLoading = false;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addSendIntakeQuestionnaireCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
) {
  const { sendIntakeQuestionnaire } = extraActions;

  builder
    .addCase(sendIntakeQuestionnaire.pending, (state) => {
      state.sendIntakeQuestionnaireLoading = true;
    })
    .addCase(
      sendIntakeQuestionnaire.fulfilled,
      (state, action: PayloadAction<IIntakeQuestionnaireBasic>) => {
        state.sendIntakeQuestionnaireLoading = false;
        if (state.intakeQuestionnairesBasicInfoProviderId === action.payload.provider_id) {
          state.intakeQuestionnairesBasicInfo.push(action.payload);
        }
        state.intakeQuestionnaires.push(action.payload);
        toast.success('Intake questionnaire sent successfully');
      },
    )
    .addCase(sendIntakeQuestionnaire.rejected, (state, action) => {
      state.sendIntakeQuestionnaireLoading = false;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addSubmitIntakeQuestionnaireCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
) {
  const { submitIntakeQuestionnaire } = extraActions;

  builder
    .addCase(submitIntakeQuestionnaire.pending, (state) => {
      state.submitIntakeQuestionnaireLoading = true;
      state.error = null;
    })
    .addCase(
      submitIntakeQuestionnaire.fulfilled,
      (state, action: PayloadAction<IIntakeQuestionnaireBasic>) => {
        state.submitIntakeQuestionnaireLoading = false;
        if (state.intakeQuestionnaireDetails) {
          state.intakeQuestionnaireDetails.status = action.payload.status;
          state.intakeQuestionnaireDetails.date_concluded = action.payload.date_concluded;
          state.intakeQuestionnaireDetails.date_concluded_utc_offset =
            action.payload.date_concluded_utc_offset;
        }
        const index = state.intakeQuestionnairesBasicInfo.findIndex(
          (row) => row.id === action.payload.id,
        );
        if (index !== -1) state.intakeQuestionnairesBasicInfo[index] = action.payload;
        toast.success('Intake questionnaire submitted successfully');
      },
    )
    .addCase(submitIntakeQuestionnaire.rejected, (state, action) => {
      state.submitIntakeQuestionnaireLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

export const intakeQuestionnaireActions = { ...slice.actions, ...extraActions };
export const intakeQuestionnaireReducer = slice.reducer;
